/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { supabase } from '../lib/supabase';
import {
  fetchGuardianProfileThunk,
  clearProfile,
} from '../store/slices/profileSlice';
import {
  setUser as setUserAction,
  setUserType as setUserTypeAction,
  setLoading as setLoadingAction,
  clearAuth,
} from '../store/slices/authSlice';
import {
  fetchChildProfileThunk,
  clearChildProfile,
} from '../store/slices/childProfileSlice';

export const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState(null); // 'guardian' or 'child'
  const [hasInitializedProfile, setHasInitializedProfile] = useState(false);
  const [hasInitializedChildProfile, setHasInitializedChildProfile] =
    useState(false);
  const dispatch = useDispatch();

  // Refs to avoid effect re-subscription loops and stale closures
  const previousUserRef = useRef(null);
  const hasInitializedProfileRef = useRef(false);
  const hasInitializedChildProfileRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    hasInitializedProfileRef.current = hasInitializedProfile;
  }, [hasInitializedProfile]);

  useEffect(() => {
    hasInitializedChildProfileRef.current = hasInitializedChildProfile;
  }, [hasInitializedChildProfile]);

  useEffect(() => {
    const determineAndInit = (nextUser, isInitialLoad = false) => {
      if (!nextUser) return;

      // Update Redux state
      dispatch(setUserAction(nextUser));

      if (nextUser.email && nextUser.email.includes('@learndle.learndle')) {
        setUserType('child');
        dispatch(setUserTypeAction('child'));
        setHasInitializedProfile(true);
        // Fetch child profile if not already initialized
        if (!hasInitializedChildProfileRef.current || isInitialLoad) {
          dispatch(fetchChildProfileThunk(nextUser.id));
          setHasInitializedChildProfile(true);
        }
      } else {
        setUserType('guardian');
        dispatch(setUserTypeAction('guardian'));
        if (!hasInitializedProfileRef.current || isInitialLoad) {
          dispatch(fetchGuardianProfileThunk(nextUser.id));
          setHasInitializedProfile(true);
        }
      }
    };

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const sessionUser = session?.user ?? null;
      setUser(sessionUser);
      if (sessionUser) {
        previousUserRef.current = sessionUser;
        determineAndInit(sessionUser, true);
      } else {
        // Clear Redux state if no user
        dispatch(clearAuth());
      }
      setLoading(false);
      dispatch(setLoadingAction(false));
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const nextUser = session?.user ?? null;
      setUser(nextUser);

      if (nextUser) {
        const prev = previousUserRef.current;
        const userChanged = !prev || prev.id !== nextUser.id;
        const isSignIn = event === 'SIGNED_IN';
        if (userChanged || isSignIn) {
          determineAndInit(nextUser, true);
        }
        previousUserRef.current = nextUser;
      } else {
        setUserType(null);
        setHasInitializedProfile(false);
        setHasInitializedChildProfile(false);
        hasInitializedProfileRef.current = false;
        hasInitializedChildProfileRef.current = false;
        previousUserRef.current = null;
        dispatch(clearProfile());
        dispatch(clearChildProfile());
        dispatch(clearAuth());
      }
      setLoading(false);
      dispatch(setLoadingAction(false));
    });

    return () => subscription.unsubscribe();
  }, [dispatch]);

  const signInWithEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signUpWithEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    return { data, error };
  };

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return { data, error };
  };

  const signInWithApple = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    // Clear profile data when signing out
    dispatch(clearProfile());
    dispatch(clearChildProfile());
    dispatch(clearAuth());
    return { error };
  };

  const value = {
    user,
    userType,
    loading,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signInWithApple,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
